import { Channel } from 'actioncable';
import { DependencyList, useEffect, useRef } from 'react';
import { useActionCable } from './ActionCableProvider';
import { websocketLog } from './websocketLog';

export type ChannelParams = Record<string, string | number | boolean | undefined>;

export interface UseActionCableChannelOptions<TMessage> {
    channel: string;
    params: ChannelParams;
    enabled?: boolean;
    onReceived: (message: TMessage) => void;
    subscriptionDeps?: DependencyList;
    logLabel?: string;
}

export const useActionCableChannel = <TMessage>({
    channel,
    params,
    enabled = true,
    onReceived,
    subscriptionDeps = [],
    logLabel
}: UseActionCableChannelOptions<TMessage>): void => {
    const cable = useActionCable();
    const onReceivedRef = useRef(onReceived);
    onReceivedRef.current = onReceived;
    const label = logLabel ?? channel;

    useEffect(() => {
        if (!enabled || !cable) {
            return;
        }

        let subscription: Channel | undefined;

        subscription = cable.subscriptions.create(
            { channel, ...params },
            {
                received: (message: TMessage) => {
                    websocketLog('message', label, { channel, message });
                    onReceivedRef.current(message);
                }
            }
        );

        websocketLog('subscribed', label, { channel, ...params });

        return () => {
            subscription?.unsubscribe();
            websocketLog('unsubscribed', label, { channel });
        };
        // subscriptionDeps drives re-subscribe when channel identity changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cable, enabled, channel, label, ...subscriptionDeps]);
};
